import { useEnv } from 'src/composables/useEnv';
import mtxApi from 'src/apis/mtx';
import { computed, onMounted, reactive, type UnwrapNestedRefs } from 'vue';
import type { AxiosError } from 'axios';
import type { Mtx, MtxMovie } from 'src/models/mtx';
import { dtFormat as dtFormatHelper } from 'src/lib/dateHelper';
import type { GeoItemMeta, GIDebugMTX, GIResource } from 'src/models/geoItem';
import type { DebugToolProps } from 'src/models/top';
import EMDebugMTXMovieLayerManager from 'src/lib/extreme_map/EMDebugMTXMovieLayerManager';
import type GIAbstractManager from 'src/lib/geo_item/GIAbstractManager';
import { readStorageItem, writeStorageItem } from 'src/services/storage';

// デバッグ用付箋レイヤーは作成・編集不可(isCreatable/isTabPanel: false)のため
// manager は実行されない。誤って呼び出された場合に気付けるよう明示的にエラーを投げる。
const createUnsupportedDebugManager = (): GIAbstractManager => ({
  getResources: () => { throw new Error('useDebugTool: manager is not supported'); },
  getResourcesByParams: () => { throw new Error('useDebugTool: manager is not supported'); },
  getResourceById: () => { throw new Error('useDebugTool: manager is not supported'); },
  mergeStateInfo: (newResources: GIResource[]) => newResources,
  createResource: () => { throw new Error('useDebugTool: manager is not supported'); },
  updateResource: () => { throw new Error('useDebugTool: manager is not supported'); },
  deleteResource: () => { throw new Error('useDebugTool: manager is not supported'); },
  filterTabPaneElements: (resources: GIResource[]) => resources,
});

const LOCAL_STORAGE_KEY = 'movie-pf-debug';
const LAYER_MANAGER_NAME = 'EMDebugMTXMovieLayerManager';

// デバッグツール専用の永続化領域。services/storage.ts の汎用アクセサを経由する。
interface DebugToolStorage {
  searchParamCandidacies?: SearchParam[];
}

const readDebugToolStorage = (): DebugToolStorage => readStorageItem<DebugToolStorage>(LOCAL_STORAGE_KEY, {});

const writeDebugToolStorage = (storage: DebugToolStorage): void => {
  writeStorageItem(LOCAL_STORAGE_KEY, storage);
};

interface Disp {
  mtxs: boolean;
  mtxTimestamps: boolean;
  movies: boolean;
  movieGeoIndices: boolean;
}

interface SearchParam {
  deviceId: string;
  startTs: string;
  endTs: string;
  searchedDeviceId?: string;
}

interface TopDebugToolState {
  search: SearchParam;
  searchParamsToRestore: SearchParam | null;
  searchParamCandidacies: SearchParam[];
  disp: Disp;
  errorMsg: string;
  activeTab: string;
  mtxs: Mtx[];
  movies: MtxMovie[];
  metaInfo: GeoItemMeta | null;
}

type DebugToolContext = {
  state: UnwrapNestedRefs<TopDebugToolState>;
  dtFormat: (dt: string) => string;
  showMTX: () => Promise<void>;
  removeMTX: () => Promise<void>;
  toggleMTXMapDisp: () => void;
  toggleMTXMapTsDisp: () => void;
  toggleMovieMapDisp: () => void;
  toggleMGIMapDisp: () => void;
  setNow: (prop: string) => void;
  saveSearchParams: () => void;
  restoreSearchParams: () => void;
};

export function useDebugTool(props: DebugToolProps): DebugToolContext {
  const { isDevelopment } = useEnv();
  const initSearchState = () => {
    return {
      deviceId: '',
      startTs: '',
      endTs: '',
      searchedDeviceId: '',
    };
  };
  const initDispState = () => {
    return {
      mtxs: true,
      mtxTimestamps: true,
      movies: true,
      movieGeoIndices: false,
    };
  };
  const state = reactive<TopDebugToolState>({
    search: initSearchState(),
    searchParamsToRestore: null,
    searchParamCandidacies: [],
    disp: initDispState(),
    errorMsg: '',
    activeTab: 'mtxs',
    mtxs: [],
    movies: [],
    metaInfo: null,
  });

  onMounted(() => {
    if (isDevelopment) {
      state.search.deviceId = 'MPF_TAKI001';
      state.search.startTs = '2019-02-10 13:00:00';
      state.search.endTs = '2019-02-10 14:10:00';
    }
    const storage = readDebugToolStorage();
    if (storage.searchParamCandidacies) {
      state.searchParamCandidacies = storage.searchParamCandidacies;
    }
    initializeDataManagers();
  });

  const carNameToDeviceId = computed(() => {
    const obj: Record<string, string> = {};
    props.cars.forEach(car => {
      if (!car.device) return;

      obj[car.device.carName] = car.deviceId;
    });
    return obj;
  });

  const initializeDataManagers = () => {
    const info = { name: 'debugMTXMovie', dispName: '付箋' };
    const layerManager = new EMDebugMTXMovieLayerManager();
    const item: GeoItemMeta = {
      name: info.name,
      dispName: info.dispName,
      layerManager,
      manager: createUnsupportedDebugManager(),
      hideToggleCheckbox: false,
      isCreatable: false,
      isTabPanel: false,
      tabLabel: '',
    };
    state.metaInfo = item;
  };
  const showMTX = async() => {
    state.errorMsg = '';
    state.mtxs = [];
    state.movies = [];
    const search = state.search;
    if (!search.deviceId || !search.startTs || !search.endTs) return;

    let deviceId = search.deviceId;
    if (carNameToDeviceId.value[deviceId]) {
      // 車両番号だったら変換してやる
      deviceId = carNameToDeviceId.value[deviceId];
    }

    state.search.searchedDeviceId = deviceId;
    const dtReg = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (!search.startTs.match(dtReg)) {
      state.errorMsg = '期間fromはyyyy-mm-dd hh:ii:ssで指定してください';
      return;
    }

    if (!search.endTs.match(dtReg)) {
      state.errorMsg = '期間toはyyyy-mm-dd hh:ii:ssで指定してください';
      return;
    }

    let result: { mtxs: Mtx[]; movies: MtxMovie[] };
    try {
      // API リクエスト境界: camelCase の状態から snake_case のリクエストへ変換する
      result = await mtxApi.fetchByDeviceId({
        device_id: deviceId,
        start_ts: search.startTs,
        end_ts: search.endTs,
      });
    } catch (e) {
      const error = e as AxiosError;
      let msg = `status:${error.response?.status}, `;
      msg += `statusText:${error.response?.statusText}, `;
      msg += `data:${JSON.stringify(error.response?.data)}`;
      state.errorMsg = msg;
      return;
    }

    if (result.mtxs.length === 0) {
      state.errorMsg = 'データが見つかりませんでした';
      return;
    }

    state.mtxs = result.mtxs;
    state.movies = result.movies;
    state.movies.forEach(e => {
      e.movieGeoIndices.sort((a, b) => {
        return a.tsMsecDiff < b.tsMsecDiff ? -1 : 1;
      });
    });

    state.disp.mtxs = true;
    state.disp.movies = true;
    state.disp.movieGeoIndices = false;
    plotInfoOnMap();
  };
  const plotInfoOnMap = () => {
    if (!state.metaInfo) return;

    const info: GIDebugMTX = {
      mtxs: state.disp.mtxs ? state.mtxs : [],
      movies: [],
    };
    info.mtxs = state.disp.mtxs ? state.mtxs : [];
    info.mtxs.forEach(mtx => {
      mtx.showTimestamp = state.disp.mtxTimestamps;
    });
    info.movies = [];
    if (state.disp.movies) {
      info.movies = state.movies.slice().map(e => {
        const obj = { ...e };
        if (!state.disp.movieGeoIndices) {
          obj.movieGeoIndices = [];
        }
        return obj;
      });
    }
    props.refExtremeMap.removeDataLayer(state.metaInfo.name);
    // reactive() の UnwrapNestedRefs 型は layerManager のクラスインスタンス型と
    // 互換にならないため、元の型へ戻す（実体は reactive proxy のまま）。
    props.refExtremeMap.showDataLayer(state.metaInfo as GeoItemMeta, info);
  };
  const removeMTX = async() => {
    for (const k in state.disp) {
      state.disp[k as keyof Disp] = false;
    }
    props.refExtremeMap.removeDataLayer(LAYER_MANAGER_NAME);
  };
  const toggleMTXMapDisp = () => {
    state.disp.mtxTimestamps = state.disp.mtxs;
    plotInfoOnMap();
  };
  const toggleMTXMapTsDisp = () => {
    plotInfoOnMap();
  };
  const toggleMovieMapDisp = () => {
    state.disp.movieGeoIndices = state.disp.movies;
    plotInfoOnMap();
  };
  const toggleMGIMapDisp = () => {
    plotInfoOnMap();
  };
  const setNow = (prop: string) => {
    state.search[prop as keyof SearchParam] =
    dtFormatHelper(new Date(), 'yyyy-mm-dd HH:MM:SS');
  };
  const saveLocalStorage = () => {
    writeDebugToolStorage({
      searchParamCandidacies: state.searchParamCandidacies,
    });
  };
  const saveSearchParams = () => {
    const maxLen = 50;
    state.searchParamCandidacies.unshift({
      deviceId: state.search.deviceId,
      startTs: state.search.startTs,
      endTs: state.search.endTs,
    });
    state.searchParamCandidacies =
      state.searchParamCandidacies.slice(0, maxLen);
    saveLocalStorage();
    alert(`検索条件を保存しました (最大${maxLen}件まで保存できます)`);
  };
  const restoreSearchParams = () => {
    if (!state.searchParamsToRestore) return;

    const params = state.searchParamsToRestore;
    state.search.deviceId = params.deviceId;
    state.search.startTs = params.startTs;
    state.search.endTs = params.endTs;
  };
  const dtFormat = (dt: string) => {
    return dtFormatHelper(dt, 'yyyy-mm-dd HH:MM:SS');
  };

  return {
    state,
    dtFormat,
    showMTX,
    removeMTX,
    toggleMTXMapDisp,
    toggleMTXMapTsDisp,
    toggleMovieMapDisp,
    toggleMGIMapDisp,
    setNow,
    saveSearchParams,
    restoreSearchParams,
  };
}
